import { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users, Plus, Trash2, UserCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = ["Viewer", "Therapist", "Family Member"];

export default function CollaboratorsTab() {
  const { child } = useApp();
  const [collaborators, setCollaborators] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("Viewer");
  const [loading, setLoading] = useState(false);

  const fetchCollaborators = async () => {
    if (!child?.id) return;
    try {
      const res = await axios.get(`${API}/children/${child.id}/collaborators`);
      setCollaborators(res.data || []);
    } catch {}
  };

  useEffect(() => { fetchCollaborators(); }, [child]);

  const addCollaborator = async () => {
    if (!name.trim() || !phone.trim() || !child) return;
    setLoading(true);
    try {
      await axios.post(`${API}/children/${child.id}/collaborators`, {
        name: name.trim(),
        phone: phone.trim(),
        role,
      });
      setName(""); setPhone("");
      fetchCollaborators();
      toast.success("Collaborator added");
    } catch { toast.error("Could not add collaborator"); }
    setLoading(false);
  };

  const removeCollaborator = async (id) => {
    if (!child) return;
    try {
      await axios.delete(`${API}/children/${child.id}/collaborators/${id}`);
      fetchCollaborators();
      toast.success("Collaborator removed");
    } catch { toast.error("Could not remove"); }
  };

  return (
    <div className="p-4 pt-6" data-testid="collaborators-tab">
      <div className="flex items-center gap-2 mb-6">
        <Users size={24} className="text-[#8E9B82]" strokeWidth={1.5} />
        <h1 className="font-outfit text-3xl font-medium text-[#2F3630] tracking-tight">Collaborators</h1>
      </div>
      <p className="font-manrope text-sm text-[#6B7264] mb-6">
        Add family members or therapists who can view {child?.name || "your child"}'s summaries and observations.
      </p>

      {/* Add Form */}
      <div className="bg-white rounded-[24px] p-5 border border-[#E2DFD8]/50 mb-6">
        <h3 className="font-outfit text-base font-medium text-[#2F3630] mb-4">Add collaborator</h3>
        <div className="space-y-3">
          <Input
            data-testid="collab-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className="rounded-xl border-[#E2DFD8] h-10 text-sm focus:ring-2 focus:ring-[#8E9B82]/20 focus:border-[#8E9B82]"
          />
          <Input
            data-testid="collab-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone number"
            className="rounded-xl border-[#E2DFD8] h-10 text-sm focus:ring-2 focus:ring-[#8E9B82]/20 focus:border-[#8E9B82]"
          />
          <div className="flex gap-2">
            {ROLE_OPTIONS.map((r) => (
              <button
                key={r}
                data-testid={`role-${r.toLowerCase()}`}
                onClick={() => setRole(r)}
                className={`px-3 py-1.5 rounded-full text-xs font-manrope transition-all ${
                  role === r ? "bg-[#8E9B82] text-white" : "bg-[#F7F5F0] text-[#6B7264] border border-[#E2DFD8]"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <Button
            data-testid="add-collab-btn"
            onClick={addCollaborator}
            disabled={!name.trim() || !phone.trim() || loading}
            className="w-full rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-10 font-outfit font-medium text-sm transition-all"
          >
            <Plus size={16} className="mr-1" /> {loading ? "Adding..." : "Add Collaborator"}
          </Button>
        </div>
      </div>

      {/* Collaborator List */}
      <div className="space-y-2">
        {collaborators.map((c) => (
          <div
            key={c.id}
            className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-[#E2DFD8]/50"
            data-testid={`collab-${c.id}`}
          >
            <div className="w-10 h-10 rounded-full bg-[#8E9B82]/10 flex items-center justify-center flex-shrink-0">
              <UserCircle size={22} className="text-[#8E9B82]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-manrope text-sm font-medium text-[#2F3630] truncate">{c.name}</p>
              <p className="font-manrope text-xs text-[#6B7264]">{c.phone} &middot; {c.role}</p>
            </div>
            <button
              data-testid={`remove-collab-${c.id}`}
              onClick={() => removeCollaborator(c.id)}
              className="text-[#C27E70] hover:text-[#A5645A] transition-colors p-1"
            >
              <Trash2 size={16} strokeWidth={1.5} />
            </button>
          </div>
        ))}
        {collaborators.length === 0 && (
          <p className="font-manrope text-sm text-[#6B7264] text-center py-4">
            No collaborators added yet.
          </p>
        )}
      </div>
    </div>
  );
}
